import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { databaseAPI } from '../../services/api';

// Async thunks
export const initializeDatabase = createAsyncThunk(
  'database/initializeDatabase',
  async (_, { rejectWithValue }) => {
    try {
      const response = await databaseAPI.initializeDatabase();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Error initializing database');
    }
  }
);

export const getColumns = createAsyncThunk(
  'database/getColumns',
  async (_, { rejectWithValue }) => {
    try {
      const response = await databaseAPI.getColumns();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Error fetching columns');
    }
  }
);

export const getRecords = createAsyncThunk(
  'database/getRecords',
  async ({ loanId, column }, { rejectWithValue }) => {
    try {
      const response = await databaseAPI.getRecords(loanId, column);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Error fetching records');
    }
  }
);

export const getLoanDetails = createAsyncThunk(
  'database/getLoanDetails',
  async ({ loanId, source = 'unknown' }, { rejectWithValue }) => {
    try {
      const response = await databaseAPI.getLoanDetails(loanId, source);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Error fetching loan details');
    }
  }
);

export const getLoanDocuments = createAsyncThunk(
  'database/getLoanDocuments',
  async ({ loanId, source = 'unknown' }, { rejectWithValue }) => {
    try {
      const response = await databaseAPI.getLoanDocuments(loanId, source);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Error fetching loan documents');
    }
  }
);

export const getAllLoans = createAsyncThunk(
  'database/getAllLoans',
  async (_, { rejectWithValue }) => {
    try {
      const response = await databaseAPI.getAllLoans();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Error fetching loan IDs');
    }
  }
);

export const getLoansForTypeahead = createAsyncThunk(
  'database/getLoansForTypeahead',
  async (_, { rejectWithValue }) => {
    try {
      const response = await databaseAPI.getLoansForTypeahead();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Error fetching loans for typeahead');
    }
  }
);

export const getLoanIdsForTypeahead = createAsyncThunk(
  'database/getLoanIdsForTypeahead',
  async (_, { rejectWithValue }) => {
    try {
      const response = await databaseAPI.getLoanIdsForTypeahead();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Error fetching loan IDs for typeahead');
    }
  }
);

export const getCaseList = createAsyncThunk(
  'database/getCaseList',
  async (loanIds, { rejectWithValue }) => {
    try {
      const response = await databaseAPI.getCaseList(loanIds);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Error fetching case list');
    }
  }
);

export const getFilteredLoans = createAsyncThunk(
  'database/getFilteredLoans',
  async (loanIds, { rejectWithValue }) => {
    try {
      const response = await databaseAPI.getFilteredLoans(loanIds);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Error fetching filtered loans');
    }
  }
);

const initialState = {
  // Data
  records: [],
  columns: ['All Fields'],
  loanDetails: null,
  loanDocuments: [],
  allLoans: [],
  loansForTypeahead: [], // Borrowers with loan_id and borrower_name for typeahead
  loanIdsForTypeahead: [], // Loan IDs for typeahead
  filteredLoans: [], // Filtered loans for case list
  combinedData: [], // Combined data for case list
  caseList: [], // Filtered case list data
  selectedLoans: [], // Selected loan IDs for filtering
  selectedBorrowers: [], // Selected borrower names for filtering
  showSystemValues: false, // Toggle for showing system values rows
  advancedFilters: [], // Array of { fieldName, values } for advanced filtering
  
  // Filters
  selectedLoanId: '',
  selectedColumn: 'All Fields',
  
  // Loading states
  loading: {
    initialize: false,
    save: false,
    getColumns: false,
    getRecords: false,
    getLoanDetails: false,
    getLoanDocuments: false,
    getAllLoans: false,
    getLoansForTypeahead: false,
    getLoanIdsForTypeahead: false,
    getFilteredLoans: false,
    getCaseList: false,
  },
  
  // Error states
  errors: {
    initialize: null,
    save: null,
    getColumns: null,
    getRecords: null,
    getLoanDetails: null,
    getLoanDocuments: null,
    getAllLoans: null,
    getLoansForTypeahead: null,
    getLoanIdsForTypeahead: null,
    getFilteredLoans: null,
    getCaseList: null,
  },
  
  // Status
  status: '',
};

const databaseSlice = createSlice({
  name: 'database',
  initialState,
  reducers: {
    setSelectedLoanId: (state, action) => {
      state.selectedLoanId = action.payload;
    },
    setSelectedColumn: (state, action) => {
      state.selectedColumn = action.payload;
    },
    setCombinedData: (state, action) => {
      state.combinedData = action.payload;
    },
    setSelectedLoans: (state, action) => {
      state.selectedLoans = action.payload;
    },
    setSelectedBorrowers: (state, action) => {
      state.selectedBorrowers = action.payload;
    },
    setShowSystemValues: (state, action) => {
      state.showSystemValues = action.payload;
    },
    setAdvancedFilters: (state, action) => {
      state.advancedFilters = action.payload;
    },
    addAdvancedFilter: (state, action) => {
      const filter = action.payload;
      const existingIndex = state.advancedFilters.findIndex(f => f.fieldName === filter.fieldName);
      if (existingIndex >= 0) {
        // Update existing filter
        state.advancedFilters[existingIndex] = filter;
      } else {
        // Add new filter
        state.advancedFilters.push(filter);
      }
    },
    removeAdvancedFilter: (state, action) => {
      const fieldName = action.payload;
      state.advancedFilters = state.advancedFilters.filter(f => f.fieldName !== fieldName);
    },
    clearAdvancedFilters: (state) => {
      state.advancedFilters = [];
    },
    clearErrors: (state) => {
      state.errors = {
        initialize: null,
        save: null,
        getColumns: null,
        getRecords: null,
        getLoanDetails: null,
      };
    },
    clearLoanData: (state) => {
      state.loanDetails = null;
      state.loanDocuments = [];
      state.selectedLoanId = null;
      state.selectedColumn = null;
    },
  },
  extraReducers: (builder) => {
    // Initialize database
    builder
      .addCase(initializeDatabase.pending, (state) => {
        state.loading.initialize = true;
        state.errors.initialize = null;
      })
      .addCase(initializeDatabase.fulfilled, (state, action) => {
        state.loading.initialize = false;
        state.status = action.payload?.message || 'Database initialized successfully';
      })
      .addCase(initializeDatabase.rejected, (state, action) => {
        state.loading.initialize = false;
        state.errors.initialize = action.payload;
      });
    
    // Get columns
    builder
      .addCase(getColumns.pending, (state) => {
        state.loading.getColumns = true;
        state.errors.getColumns = null;
      })
      .addCase(getColumns.fulfilled, (state, action) => {
        state.loading.getColumns = false;
        state.columns = action.payload?.columns || [];
      })
      .addCase(getColumns.rejected, (state, action) => {
        state.loading.getColumns = false;
        state.errors.getColumns = action.payload;
      });
    
    // Get records
    builder
      .addCase(getRecords.pending, (state) => {
        state.loading.getRecords = true;
        state.errors.getRecords = null;
      })
      .addCase(getRecords.fulfilled, (state, action) => {
        state.loading.getRecords = false;
        state.records = action.payload?.data || [];
        // Don't set status for getRecords - no need for success notification
      })
      .addCase(getRecords.rejected, (state, action) => {
        state.loading.getRecords = false;
        state.errors.getRecords = action.payload;
      });
    
    // Get loan details
    builder
      .addCase(getLoanDetails.pending, (state) => {
        state.loading.getLoanDetails = true;
        state.errors.getLoanDetails = null;
      })
      .addCase(getLoanDetails.fulfilled, (state, action) => {
        state.loading.getLoanDetails = false;
        // Store loan details in state for use by components
        state.loanDetails = action.payload?.data || action.payload || null;
      })
      .addCase(getLoanDetails.rejected, (state, action) => {
        state.loading.getLoanDetails = false;
        state.errors.getLoanDetails = action.payload;
      })
      
      // Get loan documents
      .addCase(getLoanDocuments.pending, (state) => {
        state.loading.getLoanDocuments = true;
        state.errors.getLoanDocuments = null;
      })
      .addCase(getLoanDocuments.fulfilled, (state, action) => {
        state.loading.getLoanDocuments = false;
        // Handle both possible response structures
        const documents = action.payload?.data?.documents || action.payload?.documents || [];
        console.log('🔍 [DOCUMENTS DEBUG] getLoanDocuments.fulfilled:', {
          payload: action.payload,
          documents: documents,
          count: documents.length
        });
        state.loanDocuments = documents;
      })
      .addCase(getLoanDocuments.rejected, (state, action) => {
        state.loading.getLoanDocuments = false;
        state.errors.getLoanDocuments = action.payload;
      });
    
    // Get all loans
    builder
      .addCase(getAllLoans.pending, (state) => {
        state.loading.getAllLoans = true;
        state.errors.getAllLoans = null;
      })
      .addCase(getAllLoans.fulfilled, (state, action) => {
        state.loading.getAllLoans = false;
        state.allLoans = action.payload.data || [];
      })
      .addCase(getAllLoans.rejected, (state, action) => {
        state.loading.getAllLoans = false;
        state.errors.getAllLoans = action.payload;
      });
    
    // Get loans for typeahead
    builder
      .addCase(getLoansForTypeahead.pending, (state) => {
        state.loading.getLoansForTypeahead = true;
        state.errors.getLoansForTypeahead = null;
      })
      .addCase(getLoansForTypeahead.fulfilled, (state, action) => {
        state.loading.getLoansForTypeahead = false;
        state.loansForTypeahead = action.payload.data || [];
      })
      .addCase(getLoansForTypeahead.rejected, (state, action) => {
        state.loading.getLoansForTypeahead = false;
        state.errors.getLoansForTypeahead = action.payload;
      });
    
    // Get loan IDs for typeahead
    builder
      .addCase(getLoanIdsForTypeahead.pending, (state) => {
        state.loading.getLoanIdsForTypeahead = true;
        state.errors.getLoanIdsForTypeahead = null;
      })
      .addCase(getLoanIdsForTypeahead.fulfilled, (state, action) => {
        state.loading.getLoanIdsForTypeahead = false;
        state.loanIdsForTypeahead = action.payload.data || [];
      })
      .addCase(getLoanIdsForTypeahead.rejected, (state, action) => {
        state.loading.getLoanIdsForTypeahead = false;
        state.errors.getLoanIdsForTypeahead = action.payload;
      });
    
    // Get case list
    builder
      .addCase(getCaseList.pending, (state) => {
        state.loading.getCaseList = true;
        state.errors.getCaseList = null;
      })
      .addCase(getCaseList.fulfilled, (state, action) => {
        state.loading.getCaseList = false;
        state.caseList = action.payload.data || [];
      })
      .addCase(getCaseList.rejected, (state, action) => {
        state.loading.getCaseList = false;
        state.errors.getCaseList = action.payload;
      });
    
    // Get filtered loans
    builder
      .addCase(getFilteredLoans.pending, (state) => {
        state.loading.getFilteredLoans = true;
        state.errors.getFilteredLoans = null;
      })
      .addCase(getFilteredLoans.fulfilled, (state, action) => {
        state.loading.getFilteredLoans = false;
        state.filteredLoans = action.payload.data || [];
      })
      .addCase(getFilteredLoans.rejected, (state, action) => {
        state.loading.getFilteredLoans = false;
        state.errors.getFilteredLoans = action.payload;
      });
  },
});

export const {
  setSelectedLoanId,
  setSelectedColumn,
  setCombinedData,
  setSelectedLoans,
  setSelectedBorrowers,
  setShowSystemValues,
  setAdvancedFilters,
  addAdvancedFilter,
  removeAdvancedFilter,
  clearAdvancedFilters,
  clearErrors,
  clearLoanData,
} = databaseSlice.actions;

export default databaseSlice.reducer;